import { useQuery } from "@apollo/client";
import { Check, Close, InfoOutlined } from "@mui/icons-material";
import {
  MenuItem,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Paper,
  Box,
  Tooltip,
  List,
  ListItem,
  Divider,
  ListSubheader,
} from "@mui/material";
import { FormikProvider } from "formik";
import { useSnackbar } from "notistack";
import { ReactElement, useState } from "react";

import { STATES_WITH_AREA_CODES } from "@stw/states";

import {
  RegistrationSubmittedToTcrEvent,
  GetRegistrationForStaffQuery,
  LookupTcrBrandByEinQuery,
  SubmitBrandToTcrMutation,
  SubmitBrandToTcrMutationVariables,
  TenDlcOrgType,
  AttMetadata,
  TcrUseCase,
  TmobileMetadata,
  ListTenantsWithSubaccountsQuery,
  ImportCvTokenMutation,
  ImportCvTokenMutationVariables,
  GetLatestEvpReportQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { ButtonWithMenu } from "~frontend/components/ButtonWithMenu";
import CollapsibleDrawer from "~frontend/components/CollapsibleDrawer";
import { FlexBox } from "~frontend/components/FlexBox";
import InfoToolip from "~frontend/components/InfoTooltip";
import Table from "~frontend/components/Table";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import { FormikErrorBanner, SubmitButton } from "~frontend/components/forms";
import {
  SubaccountDropdown,
  TenantDropdown,
} from "~frontend/components/forms/ClientDropdowns";
import {
  FormikSearchableSelect,
  FormikSelect,
  MultiSelect,
} from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import {
  GET_LATEST_EVP_REPORT,
  GET_REGISTRATION_FOR_STAFF,
  IMPORT_CV_TOKEN,
  LOOKUP_TCR_BRAND_BY_EIN,
  SUBMIT_BRAND_TO_TCR,
} from "~frontend/graphql/tenDlcRegistrations";
import { LIST_TENANTS_WITH_SUBACCOUNTS } from "~frontend/graphql/tenant";
import { useStaffEmails } from "~frontend/hooks/useStaffEmails";
import { FOCUS_AREAS } from "~frontend/routes/subaccounts/phoneNumberList/TenDlc/InternalRegistrationForm";
import {
  ORG_TYPE_MENU_OPTIONS,
  TEXTS_PER_MONTH_MENU_OPTIONS,
  TCR_VERTICALS,
} from "~frontend/routes/subaccounts/phoneNumberList/TenDlc/constants";
import { palette } from "~frontend/theme";
import { STATE_MENU_OPTIONS } from "~frontend/util/stateMenuOptions";
import useMutation from "~frontend/util/useMutation";

import { COMPLIANCE_STAFF_EMAILS } from "../constants";
import { EventLog } from "./EventLog";
import { ImportBrandFromTcrModal } from "./ImportBrandFromTcrModal";
import {
  initialBrandValues,
  InputRow,
  useTenDlcContext,
} from "./TenDlcProvider";

const QualifyCheckbox = ({ qualify }: { qualify?: boolean }) => {
  if (qualify) {
    return <Check color="success" />;
  }

  return <Close color="error" />;
};

export const BrandDetails = ({
  brand,
}: {
  brand: GetRegistrationForStaffQuery["getRegistrationForStaff"];
}): ReactElement => {
  const lastSubmitEvent = brand.events?.find(
    event => event.type === "SUBMITTED_TO_TCR"
  ) as RegistrationSubmittedToTcrEvent | undefined;

  return (
    <Paper elevation={2} sx={{ px: 2 }}>
      <TwoColumnDisplay
        labelWidth="200px"
        showBottomBorder={false}
        showTopBorder={false}
        rows={[
          {
            key: "tcrBrandId",
            label: "TCR Brand ID",
            content: brand.tcrBrandId,
          },
          {
            key: "tcrIdentityStatus",
            label: (
              <FlexBox gap={1}>
                <Box>TCR Identity Status</Box>
                <Tooltip
                  title={
                    <List>
                      <ListItem>
                        UNVERIFIED: Indicates the brand registered with
                        incorrect or incomplete information and TCR could not
                        verify the brand identity, such brands can move from
                        UNVERIFIED to VERIFIED status by updating their
                        information and undergoing the verification process
                        again
                      </ListItem>
                      <ListItem>
                        VERIFIED: Indicates the existence of the brand has been
                        confirmed through the data submitted during brand
                        registration
                      </ListItem>
                      <ListItem>
                        VETTED_VERIFIED: Indicates the registered brand has been
                        successfully vetted by a third-party vetting provider. A
                        brand may acquire this status by either applying or
                        importing a third-party standard vet. The vetted and
                        verified status may grant brands access to more
                        use-cases and higher rate limits across participating
                        MNO networks
                      </ListItem>
                    </List>
                  }
                >
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </FlexBox>
            ),
            content: brand.tcrIdentityStatus ?? "unknown",
          },
          {
            key: "submittedToTcrAt",
            label: "Submitted to TCR",
            content: lastSubmitEvent?.createdAt
              ? `${new Date(lastSubmitEvent.createdAt).toLocaleDateString()} ${new Date(lastSubmitEvent.createdAt).toLocaleTimeString()} by ${lastSubmitEvent.data.submittedByStaffEmail}`
              : "",
          },
        ]}
      />
      <Divider />
      <Box sx={{ pb: 2, pt: 1 }}>
        <CollapsibleDrawer
          title={
            <Typography variant="body1Bold" sx={{ color: palette.navy }}>
              Eligible Use Cases
            </Typography>
          }
          startOpen={false}
          sxHeader={{ border: 0 }}
        >
          <Table<TcrUseCase & { id: string }>
            columns={[
              {
                key: "usecase",
                title: "Use Case",
                renderValue: row => {
                  return row.usecase;
                },
              },

              {
                key: "at&t",
                title: "AT&T",
                renderValue: row => {
                  const att = row.mnoMetadata?.find(
                    mno => mno.mno === "AT&T"
                  ) as AttMetadata | undefined;
                  const qualify = !!att?.qualify;
                  return (
                    <>
                      <QualifyCheckbox qualify={qualify} />
                      {qualify && (
                        <FlexBox direction="column" sx={{ minWidth: "100px" }}>
                          <Box>{att.tpm || "--"} TPM</Box>
                          <Box>{att.mmsTpm || "--"} MMS TPM</Box>
                        </FlexBox>
                      )}
                    </>
                  );
                },
              },
              {
                key: "t-mo",
                title: "T-Mobile",
                renderValue: row => {
                  const tmo = row.mnoMetadata?.find(
                    mno => mno.mno === "T-Mobile"
                  ) as TmobileMetadata | undefined;
                  const qualify = !!tmo?.qualify;
                  return (
                    <>
                      <QualifyCheckbox qualify={qualify} />
                      {qualify && (
                        <FlexBox direction="column" sx={{ minWidth: "100px" }}>
                          <Box>Daily cap: {tmo.brandDailyCap || "--"}</Box>
                          <Box>Brand tier: {tmo.brandTier || "--"}</Box>
                        </FlexBox>
                      )}
                    </>
                  );
                },
              },
              {
                key: "verizon",
                title: "Verizon",
                renderValue: row => {
                  return (
                    <QualifyCheckbox
                      qualify={
                        !!row.mnoMetadata?.find(mno => mno.mno === "Interop")
                          ?.qualify
                      }
                    />
                  );
                },
              },
              {
                key: "interop",
                title: "Interop",
                renderValue: row => {
                  return (
                    <QualifyCheckbox
                      qualify={
                        !!row.mnoMetadata?.find(mno => mno.mno === "Interop")
                          ?.qualify
                      }
                    />
                  );
                },
              },
              {
                key: "clearsky",
                title: "ClearSky",
                renderValue: row => {
                  return (
                    <QualifyCheckbox
                      qualify={
                        !!row.mnoMetadata?.find(mno => mno.mno === "ClearSky")
                          ?.qualify
                      }
                    />
                  );
                },
              },
              {
                key: "uscellular",
                title: "US Cellular",
                renderValue: row => {
                  return (
                    <QualifyCheckbox
                      qualify={
                        !!row.mnoMetadata?.find(
                          mno => mno.mno === "US Cellular"
                        )?.qualify
                      }
                    />
                  );
                },
              },
              {
                key: "fees",
                title: (
                  <>
                    Fees
                    <InfoToolip sx={{ marginBottom: "-7px" }}>
                      annual/quarterly/monthly, USD
                    </InfoToolip>
                  </>
                ),

                renderValue: row => {
                  return (
                    <Box sx={{ minWidth: "100px" }}>
                      ${row.annualFee} / ${row.quarterlyFee} / ${row.monthlyFee}
                    </Box>
                  );
                },
              },
            ]}
            rows={(brand.tcrUseCases || []).map(usecase => ({
              ...usecase,
              id: usecase.usecase,
            }))}
            noFooter
          />
        </CollapsibleDrawer>
      </Box>
    </Paper>
  );
};

export const BrandForm = ({
  header,
}: {
  header: ReactElement;
}): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { brandFormik, brand } = useTenDlcContext()!;
  const {
    values,
    setFieldValue,
    dirty,
    resetForm,
    isSubmitting,
    isValid,
    handleChange,
    submitForm,
    errors,
    validateForm,
    setErrors,
    setFieldTouched,
  } = brandFormik;

  const {
    loading: staffEmailLoading,
    error: staffEmailError,
    data: staffEmailData,
    prioritizeEmails,
  } = useStaffEmails();

  const { data: tcrBrands, loading } = useQuery<LookupTcrBrandByEinQuery>(
    LOOKUP_TCR_BRAND_BY_EIN,
    {
      variables: {
        input: {
          ein: brand.ein,
        },
      },
      skip: !!brand.tcrBrandId,
    }
  );
  const [showImportModal, setShowImportModal] = useState(false);

  const {
    data: tenantsData,
    loading: tenantsLoading,
    error: tenantsError,
  } = useQuery<ListTenantsWithSubaccountsQuery>(LIST_TENANTS_WITH_SUBACCOUNTS);

  const validateBeforeTcr = async (): Promise<{ hasErrors: boolean }> => {
    await setFieldValue("validateRequiredFields", true);

    const formikErrors = await validateForm();
    if (Object.keys(formikErrors).length > 0) {
      setErrors(formikErrors);
      Object.keys(formikErrors).forEach(
        async field => await setFieldTouched(field, true)
      );
      return { hasErrors: true };
    }

    return { hasErrors: false };
  };

  const [submitToTcr, { loading: isSubmittingToTcr }] = useMutation<
    SubmitBrandToTcrMutation,
    SubmitBrandToTcrMutationVariables
  >(SUBMIT_BRAND_TO_TCR, {
    onErrorSnackbarMessage: "Failed to submit to TCR",
    refetchQueries: [GET_REGISTRATION_FOR_STAFF],
    awaitRefetchQueries: true,
    variables: {
      input: {
        brandRegistrationId: brand.id,
      },
    },
  });

  const handleSubmitToTcr = async () => {
    const { hasErrors } = await validateBeforeTcr();
    if (hasErrors) {
      enqueueSnackbar("Please fix the errors before submitting to TCR", {
        variant: "error",
      });
      return;
    }

    resetForm();
    const { data, errors } = await submitToTcr();
    if (errors) {
      enqueueSnackbar(
        <FlexBox gap={1} direction="column">
          <Typography variant="h2Bold" component="div">
            Failed to submit brand to TCR:
          </Typography>
          {errors.map(error => (
            <Typography key={error.message} variant="body1">
              {error.message}
            </Typography>
          ))}
        </FlexBox>,
        {
          variant: "error",
        }
      );
    } else if (data?.submitBrandToTcr) {
      resetForm({ values: initialBrandValues(data.submitBrandToTcr) });
      enqueueSnackbar("Brand submitted to TCR successfully", {
        variant: "success",
      });
    } else {
      enqueueSnackbar(
        "Unexpected issue encountered, please reach out to engineering",
        {
          variant: "warning",
        }
      );
    }
  };

  const { data: evpReportData } = useQuery<GetLatestEvpReportQuery>(
    GET_LATEST_EVP_REPORT,
    {
      variables: {
        input: {
          tcrBrandId: brand.tcrBrandId,
        },
      },
    }
  );
  const hasExpiredCvToken =
    evpReportData?.getLatestEvpReport.vettingStatus === "EXPIRED";

  const [importCvToken, { loading: isImportingCvToken }] = useMutation<
    ImportCvTokenMutation,
    ImportCvTokenMutationVariables
  >(IMPORT_CV_TOKEN, {
    onErrorSnackbarMessage: "Failed to import new token to TCR",
    refetchQueries: [GET_REGISTRATION_FOR_STAFF],
    awaitRefetchQueries: true,
    variables: {
      input: {
        brandRegistrationId: brand.id,
      },
    },
  });

  return (
    <AsyncWrapper
      loading={[staffEmailLoading, tenantsLoading]}
      errors={[staffEmailError, tenantsError]}
      data={{ staffEmailList: staffEmailData, tenantsList: tenantsData }}
    >
      {({ staffEmailList, tenantsList }) => (
        <FormikProvider value={brandFormik}>
          <FlexBox direction="column">
            <ImportBrandFromTcrModal
              brandRegistrationId={brand.id}
              isOpen={showImportModal}
              onClose={() => setShowImportModal(false)}
              tcrBrands={tcrBrands?.lookupTcrBrandByEin || []}
            />
            <FlexBox
              justifyContent="space-between"
              alignItems="center"
              sx={{ pb: 1 }}
            >
              {header}
              <ButtonWithMenu
                primaryButton={
                  <SubmitButton
                    disabled={!dirty}
                    onClick={async () => {
                      await setFieldValue("validateRequiredFields", false);
                      await submitForm();
                    }}
                    submitText="Save changes"
                    loadingText="Saving..."
                    isSubmitting={isSubmitting}
                  />
                }
                menuItems={[
                  {
                    key: "copy-permalink",
                    label: "Copy link",
                    onClick: async () => {
                      await navigator.clipboard.writeText(
                        `${window.location.origin}/staff/compliance-registrations/ten-dlc/${brand.id}`
                      );
                      enqueueSnackbar("Link copied to clipboard");
                    },
                  },
                  {
                    key: "reset",
                    label: "Reset changes",
                    onClick: () => resetForm(),
                    visible: dirty,
                    disabled: isSubmitting,
                  },
                  {
                    key: "lookup-in-tcr-by-ein",
                    label: (
                      <FlexBox direction="column">
                        <Typography variant="body1">
                          Import existing from TCR
                        </Typography>
                        <Typography variant="body2">
                          {loading
                            ? "loading..."
                            : `Found ${tcrBrands?.lookupTcrBrandByEin?.length ?? "0"}`}
                        </Typography>
                      </FlexBox>
                    ),
                    visible: !brand.tcrBrandId,
                    disabled: loading,
                    onClick: () => {
                      setShowImportModal(true);
                    },
                  },
                  {
                    key: "submit-to-tcr",
                    label: "Submit to TCR",
                    disabled: !isValid || dirty || isSubmittingToTcr,
                    visible: !brand.tcrBrandId,
                    onClick: async () => await handleSubmitToTcr(),
                  },
                  {
                    key: "view-in-tcr",
                    label: "View in TCR",
                    onClick: () => window.open(brand.tcrUrl!, "_blank"),
                    visible: !!brand.tcrBrandId && !!brand.tcrUrl,
                  },
                  {
                    key: "resubmit-cv-token",
                    label: "Resubmit CV token",
                    onClick: async () => {
                      await importCvToken();
                    },
                    visible: hasExpiredCvToken && !!brand.cvToken,
                    disabled: isImportingCvToken,
                  },
                ]}
              />
            </FlexBox>

            <FormikErrorBanner />

            {brand.tcrBrandId ? (
              <Box sx={{ py: 2 }}>
                <BrandDetails brand={brand} />
              </Box>
            ) : null}

            {brand.events && brand.events.length > 0 && (
              <Box sx={{ py: 2 }}>
                <EventLog events={brand.events} title="Brand Event Log" />
              </Box>
            )}

            <InputRow>
              <FormikSelect
                label="Assigned to staff member"
                name="assignedToStaffEmail"
                onChange={handleChange}
                withNoneOption
                fast
              >
                {prioritizeEmails(staffEmailList, COMPLIANCE_STAFF_EMAILS).map(
                  staffEmail => (
                    <MenuItem key={staffEmail.email} value={staffEmail.email}>
                      {staffEmail.fullName}
                    </MenuItem>
                  )
                )}
              </FormikSelect>
            </InputRow>

            <InputRow>
              <TenantDropdown
                value={values.tenantId}
                tenants={tenantsList?.listTenants || []}
                onChange={async tenantId => {
                  await setFieldValue("tenantId", tenantId);
                  await setFieldValue("subaccountId", "");
                }}
              />
              <SubaccountDropdown
                selectedTenantId={values.tenantId}
                value={values.subaccountId}
                tenants={tenantsList?.listTenants || []}
                onChange={async subaccountId => {
                  await setFieldValue("subaccountId", subaccountId);
                }}
              />
            </InputRow>

            <Divider sx={{ pt: 1, mb: 2 }} />

            <InputRow>
              <FormikTextField
                name="organizationName"
                label="Organization Name"
                size="small"
                onChange={handleChange}
                fast
              />
              <FormikTextField
                label="Doing Business As (DBA)"
                name="displayName"
                onChange={handleChange}
                fast
              />
            </InputRow>

            <InputRow>
              <FormikSelect
                label="Organization Type"
                name="orgType"
                onChange={handleChange}
                fast
                sx={{
                  "& .MuiSelect-icon": {
                    backgroundColor: palette.white,
                  },
                }}
              >
                {ORG_TYPE_MENU_OPTIONS.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </FormikSelect>
              <FormikTextField
                label="EIN"
                name="ein"
                onBlur={async e => {
                  const numsOnly = e.target.value.replace("-", "");
                  const newValue = numsOnly
                    ? `${numsOnly.slice(0, 2)}-${numsOnly.slice(2)}`
                    : "";
                  await setFieldValue("ein", newValue);
                }}
                onChange={handleChange}
                fast
              />
            </InputRow>

            {values.orgType === TenDlcOrgType.Political ? (
              <FormikTextField
                label="Campaign Verify Token"
                name="cvToken"
                onBlur={async e => {
                  const newValue = e.target.value.trim();
                  await setFieldValue("cvToken", newValue);
                }}
                onChange={handleChange}
                fast
                multiline
                minRows={3}
                maxRows={10}
                fullWidth
              />
            ) : (
              ""
            )}
            <InputRow>
              <FormikTextField
                label="Your Website"
                name="website"
                value={values.website}
                fast
              />
            </InputRow>
            <InputRow>
              <FormikTextField
                label="Street One"
                name="addressOne"
                onChange={handleChange}
                fast
              />
              <FormikTextField
                label="Street Two"
                name="addressTwo"
                onChange={handleChange}
                fast
              />
            </InputRow>
            <InputRow>
              <FormikTextField
                label="City"
                name="city"
                fast
                fullWidth
                sx={{ flexGrow: 1 }}
              />
            </InputRow>
            <FlexBox gap={4} sx={{ width: "100%" }}>
              <FormikSearchableSelect
                label="State"
                name="state"
                fast
                options={STATE_MENU_OPTIONS.map(option => {
                  return { label: option.label, id: option.value };
                })}
                fullWidth
              />
              <FormikTextField label="Postal Code" name="postalCode" fast />
            </FlexBox>

            <Typography variant="h2Bold">Contact Information</Typography>
            <InputRow>
              <FormikTextField
                label="Organization Contact Name"
                name="organizationContactName"
                onChange={handleChange}
                fast
              />
              <FormikTextField
                label="Organization Contact Email"
                name="organizationContactEmail"
                type="email"
                onChange={handleChange}
                fast
              />
            </InputRow>
            <InputRow>
              <FormikTextField
                label="Organization Contact Phone Number"
                name="organizationContactPhone"
                onChange={handleChange}
                fast
              />
              <div style={{ width: "48%" }} />
            </InputRow>
            <Typography variant="h2">
              Is the organization contact the person Scale to Win should reach
              out to with questions about this registration?
            </Typography>
            <RadioGroup
              row={true}
              sx={{
                display: "flex",
                flexWrap: "wrap",
              }}
              value={values.orgContactIsPrimaryContact}
              onChange={async e => {
                await setFieldValue(
                  "orgContactIsPrimaryContact",
                  e.target.value === "true"
                );
              }}
              name="orgContactIsPrimaryContact"
            >
              <FormControlLabel value={true} control={<Radio />} label="Yes" />

              <FormControlLabel value={false} control={<Radio />} label="No" />
            </RadioGroup>

            {values.orgContactIsPrimaryContact ? null : (
              <>
                <InputRow>
                  <FormikTextField
                    label="Primary Contact Name"
                    name="primaryContactName"
                    onChange={handleChange}
                    fast
                  />
                  <FormikTextField
                    label="Primary Contact Email"
                    name="primaryContactEmail"
                    type="email"
                    onChange={handleChange}
                    fast
                  />
                </InputRow>
                <InputRow>
                  <FormikTextField
                    label="Primary Contact Phone Number"
                    name="primaryContactPhone"
                    onChange={handleChange}
                    fast
                  />
                  <div style={{ width: "48%" }} />
                </InputRow>
              </>
            )}
            <Typography variant="h2Bold" component="div" sx={{ pt: 3 }}>
              Texting Program
            </Typography>
            <InputRow>
              <FormikSelect
                label="Preferred Area Code"
                name="preferredAreaCode"
                withNoneOption
                noneOptionLabel="Select preferred area code..."
                onChange={handleChange}
                fast
              >
                {STATES_WITH_AREA_CODES.flatMap(state => [
                  <ListSubheader>{state.name}</ListSubheader>,
                  ...state.areaCodes.map(areaCode => (
                    <MenuItem value={areaCode}>{areaCode}</MenuItem>
                  )),
                ])}
              </FormikSelect>
              <MultiSelect
                label="Alternate Area Codes"
                name="desiredAreaCodes"
                value={values.desiredAreaCodes}
                error={!!errors.desiredAreaCodes}
                helperText={(errors.desiredAreaCodes as string) || ""}
                onChange={handleChange}
              >
                {STATES_WITH_AREA_CODES.flatMap(state => [
                  <ListSubheader>{state.name}</ListSubheader>,
                  ...state.areaCodes.map(areaCode => (
                    <MenuItem value={areaCode}>{areaCode}</MenuItem>
                  )),
                ])}
              </MultiSelect>
              <FormikSelect
                label="Expected Texts per Month"
                name="expectedTexts"
                onChange={handleChange}
                fast
              >
                {TEXTS_PER_MONTH_MENU_OPTIONS.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </FormikSelect>
            </InputRow>

            {values.orgType === TenDlcOrgType.Private ? (
              <InputRow>
                <FormikSelect
                  renderValue={value => {
                    const focusArea =
                      TCR_VERTICALS[value as keyof typeof TCR_VERTICALS];
                    return focusArea ? focusArea.displayName : "";
                  }}
                  label="Focus Area"
                  name="focusArea"
                  onChange={handleChange}
                  fast
                >
                  {FOCUS_AREAS.map(area => (
                    <MenuItem key={area.value} value={area.value}>
                      {area.label}
                    </MenuItem>
                  ))}
                </FormikSelect>
              </InputRow>
            ) : (
              ""
            )}

            <FlexBox gap={2} justifyContent="flex-end">
              <Button
                disabled={isSubmitting || !dirty}
                onClick={() => resetForm()}
              >
                Reset changes
              </Button>
              <SubmitButton
                disabled={!dirty}
                onClick={async () => {
                  await setFieldValue("validateRequiredFields", false);
                  await submitForm();
                }}
                submitText="Save changes"
                loadingText="Saving..."
                isSubmitting={isSubmitting}
              />
            </FlexBox>
          </FlexBox>
        </FormikProvider>
      )}
    </AsyncWrapper>
  );
};
